import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  Refresh
} from '@mui/icons-material';
import moment from 'moment';
import axios from 'axios';
import Sidebar from './common/Sidebar';
import WeeklyScheduleSelector from './schedule/WeeklyScheduleSelector';
import MobileScheduleSelector from './schedule/MobileScheduleSelector';
import AdminScheduleManager from './schedule/AdminScheduleManager';
import { getUserData } from '../services/storageService';
import platformScheduleService from '../services/platformScheduleService';
import { createSchedule, getAllSchedules, deleteSchedule } from '../services/scheduleService';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend-c-0mxm.onrender.com/api';

// Tạo instance axios
const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem('user')) || {};
    if (user.token) {
      config.headers['Authorization'] = `Bearer ${user.token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const SimpleLivestreamScheduler = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [weekStartDate, setWeekStartDate] = useState(moment().startOf('week').add(1, 'day'));
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const isAdmin = user?.role === 'admin';
  
  useEffect(() => {
    const userData = getUserData();
    setUser(userData);
  }, []);
  
  useEffect(() => {
    if (user) {
      fetchUsers();
      fetchSchedules();
    }
  }, [user, weekStartDate]);
  
  // Fetch all users (admin only)
  const fetchUsers = async () => {
    if (!isAdmin) {
      setUsers([user]);
      return;
    }
    
    try {
      const response = await api.get('/auth/users');
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      showNotification('Lỗi khi tải danh sách nhân viên', 'error');
    }
  };
  
  // Fetch schedules for the week
  const fetchSchedules = async () => {
    setLoading(true);
    try {
      const startDate = weekStartDate.format('YYYY-MM-DD');
      const endDate = weekStartDate.clone().add(6, 'days').format('YYYY-MM-DD');
      
      // Use unified old API for consistency
      const result = await getAllSchedules(startDate, endDate);
      
      if (result.success) {
        let allSchedules = result.data || [];
        
        // For employees: Keep ALL schedules (to show who else registered)
        // But they can only edit/delete their own schedules
        // Admin sees all schedules
        
        setSchedules(allSchedules);
      } else {
        console.error('Error fetching schedules:', result.error);
        showNotification(result.error?.message || 'Lỗi khi tải lịch', 'error');
        setSchedules([]);
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
      showNotification('Lỗi khi tải lịch', 'error');
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Show notification
  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };
  
  // Handle submit schedules (for users)
  const handleSubmitSchedules = async (schedulesToCreate) => {
    setLoading(true);
    try {
      console.log('📝 Đang tạo lịch:', schedulesToCreate);
      
      // Create schedules one by one using old API for consistency
      const promises = schedulesToCreate.map(schedule => {
        // Normalize date
        const normalizedDate = new Date(schedule.date);
        normalizedDate.setHours(12, 0, 0, 0);
        
        const scheduleData = {
          ...schedule,
          date: normalizedDate.toISOString()
        };
        
        console.log('📤 Gửi lịch:', scheduleData);
        return createSchedule(scheduleData);
      });
      
      const results = await Promise.all(promises);
      
      console.log('📥 Kết quả:', results);
      
      // Check results and show detailed errors
      const successCount = results.filter(r => r.success).length;
      const failCount = results.length - successCount;
      
      if (failCount > 0) {
        // Log failed schedules
        results.forEach((result, index) => {
          if (!result.success) {
            console.error(`❌ Lịch ${index + 1} lỗi:`, result.error);
          }
        });
        
        if (successCount > 0) {
          showNotification(`Đã đăng ký ${successCount}/${results.length} lịch. ${failCount} lịch bị lỗi.`, 'warning');
        } else {
          // Show first error message
          const firstError = results.find(r => !r.success);
          const errorMsg = firstError?.error?.message || 'Không thể đăng ký lịch';
          showNotification(errorMsg, 'error');
        }
        fetchSchedules();
      } else {
        showNotification(`Đã đăng ký ${schedulesToCreate.length} lịch thành công!`, 'success');
        fetchSchedules();
      }
    } catch (error) {
      console.error('❌ Error creating schedules:', error);
      showNotification(error.message || 'Lỗi khi đăng ký lịch', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle edit schedule (admin)
  const handleEditSchedule = async (scheduleId, updateData) => {
    setLoading(true);
    try {
      await platformScheduleService.updateSchedule(
        updateData.platform,
        scheduleId,
        updateData
      );
      
      showNotification('Đã cập nhật lịch thành công!', 'success');
      fetchSchedules();
    } catch (error) {
      console.error('Error updating schedule:', error);
      showNotification(error.message || 'Lỗi khi cập nhật lịch', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle delete schedule (admin)
  const handleDeleteSchedule = async (scheduleId) => {
    setLoading(true);
    try {
      // Use old API for consistency
      const result = await deleteSchedule(scheduleId);
      
      if (result.success) {
        showNotification('Đã xóa lịch thành công!', 'success');
        fetchSchedules();
      } else {
        showNotification(result.error?.message || 'Không thể xóa lịch', 'error');
        fetchSchedules(); // Still refresh to show current state
      }
    } catch (error) {
      console.error('Error deleting schedule:', error);
      showNotification(error.message || 'Lỗi khi xóa lịch', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle add schedule (admin)
  const handleAddSchedule = async (scheduleData) => {
    setLoading(true);
    try {
      await platformScheduleService.createScheduleForPlatform(
        scheduleData.platform,
        scheduleData
      );
      
      showNotification('Đã thêm lịch thành công!', 'success');
      fetchSchedules();
    } catch (error) {
      console.error('Error adding schedule:', error);
      showNotification(error.message || 'Lỗi khi thêm lịch', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle approve schedule (admin)
  const handleApproveSchedule = async (scheduleId) => {
    const schedule = schedules.find(s => s._id === scheduleId);
    if (schedule) {
      await handleEditSchedule(scheduleId, { ...schedule, status: 'approved' });
    }
  };
  
  // Handle reject schedule (admin)
  const handleRejectSchedule = async (scheduleId) => {
    const schedule = schedules.find(s => s._id === scheduleId);
    if (schedule) {
      await handleEditSchedule(scheduleId, { ...schedule, status: 'rejected' });
    }
  };
  
  // Navigate week
  const goToPreviousWeek = () => {
    setWeekStartDate(weekStartDate.clone().subtract(7, 'days'));
  };
  
  const goToNextWeek = () => {
    setWeekStartDate(weekStartDate.clone().add(7, 'days'));
  };
  
  const goToCurrentWeek = () => {
    setWeekStartDate(moment().startOf('week').add(1, 'day'));
  };
  
  if (!user) {
    return null;
  }
  
  return (
    <Sidebar>
          {/* Header - Responsive */}
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 2,
              borderRadius: { xs: 0, sm: 2 },
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  {isAdmin ? '📊 Quản lý lịch Livestream' : '📅 Đăng ký lịch Livestream'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Tuần: <strong>{weekStartDate.format('DD/MM')} - {weekStartDate.clone().add(6, 'days').format('DD/MM/YYYY')}</strong>
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={0.5} justifyContent={{ xs: 'center', sm: 'flex-end' }}>
                <IconButton
                  size="small"
                  onClick={goToPreviousWeek}
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                >
                  <ChevronLeft />
                </IconButton>
                
                <Button
                  size="small"
                  onClick={goToCurrentWeek}
                  startIcon={<Today />}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                  }}
                >
                  Tuần này
                </Button>
                
                <IconButton
                  size="small"
                  onClick={goToNextWeek}
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                >
                  <ChevronRight />
                </IconButton>
                
                <IconButton
                  size="small"
                  onClick={fetchSchedules}
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                >
                  <Refresh />
                </IconButton>
              </Stack>
            </Stack>
          </Paper>
          
          {/* Content */}
          {isAdmin ? (
            <AdminScheduleManager
              schedules={schedules}
              users={users}
              weekStartDate={weekStartDate.toDate()}
              onEdit={handleEditSchedule}
              onDelete={handleDeleteSchedule}
              onAdd={handleAddSchedule}
              onApprove={handleApproveSchedule}
              onReject={handleRejectSchedule}
              onRefresh={fetchSchedules}
            />
          ) : (
            <MobileScheduleSelector
              userId={user.userId}
              userName={user.fullName}
              userRole={user.role}
              weekStartDate={weekStartDate.toDate()}
              existingSchedules={schedules}
              onSubmit={handleSubmitSchedules}
              onRefresh={fetchSchedules}
              onDelete={handleDeleteSchedule}
              onWeekChange={(direction) => {
                if (direction === 'next') goToNextWeek();
                else if (direction === 'prev') goToPreviousWeek();
              }}
            />
          )}
      
      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      
      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Sidebar>
  );
};

export default SimpleLivestreamScheduler;

